/**
 * Sprite-frame composition: the region_rect and hframes/vframes UV math for
 * Sprite2D and Sprite3D. Godot's SpriteBase takes a base rect (the region when
 * `region_enabled`, else the full texture) and then subdivides it by the frame
 * grid, so region and frames compose.
 *
 * The frame's UV window is set apart from the sampler clone on purpose. three keys a
 * GPU texture by source and sampler state, never by offset or repeat, so a frame
 * change moves the window on the clone already drawn and uploads nothing.
 */
import { FULL_UV_WINDOW, regionUvWindow } from './uvWindow';
import { frameCoords } from '../godot/scene/spriteFrames';
/**
 * A frame that is the whole of the texture it names, as an AnimatedSprite's frame is:
 * `useTexture2D` crops a sheet cell (AtlasTexture) to its own texture, so nothing is windowed.
 */
export const WHOLE_FRAME = Object.freeze({
    region_enabled: false,
    hframes: 1,
    vframes: 1,
    frame: 0,
});
/**
 * The current frame's UV window over `texture`: the region-then-frame-grid composition, over the
 * full-image window.
 */
export function frameUvWindow(texture, props) {
    const base = regionWindow(texture, props) ?? FULL_UV_WINDOW;
    return props.hframes > 1 || props.vframes > 1 ? cellWindow(base, props) : base;
}
/**
 * Frame size in texture pixels: the base rect (region when enabled, else `reportedSize`, the size
 * Godot reports for the texture), subdivided by the frame grid. Falls back to 1×1 without a
 * texture so placeholders keep a sensible scale.
 */
export function frameSizePx(reportedSize, props) {
    const H = Math.max(1, props.hframes);
    const V = Math.max(1, props.vframes);
    let pxW = reportedSize?.width ?? 1;
    let pxH = reportedSize?.height ?? 1;
    if (props.region_enabled && props.region_rect && reportedSize) {
        pxW = props.region_rect.width;
        pxH = props.region_rect.height;
    }
    return { width: pxW / H, height: pxH / V };
}
/**
 * Godot's `texture_repeat` (`sprite_3d.cpp:163`): repeat only where the frame's UV
 * window leaves `[0, 1]`, on strict tests. Flips only swap the box's diagonal,
 * and our v-window is Godot's mirrored about 0.5, which the test is symmetric under.
 */
export function spriteWrapMode(texture, props) {
    if (!texture)
        return 'clamp';
    const uv = frameUvWindow(texture, props);
    const outside = (min, size) => min < 0 || min + size > 1;
    return outside(uv.offset.x, uv.repeat.x) || outside(uv.offset.y, uv.repeat.y) ? 'repeat' : 'clamp';
}
/**
 * The region's window, or null for no region. Without the image's dimensions there is no
 * sub-rectangle, so the sprite renders the full texture (the linter warns on region misuse).
 */
function regionWindow(texture, props) {
    const rect = props.region_enabled ? props.region_rect : undefined;
    const image = texture?.image;
    if (!rect || !image?.width || !image.height)
        return null;
    return regionUvWindow({ x: rect.x, y: rect.y, w: rect.width, h: rect.height }, { x: image.width, y: image.height });
}
/**
 * The frame's cell of the grid over `base`, the full image or the region's window. three's `v` runs
 * bottom-up and Godot's image `y` top-down, so row 0 sits at the top of `base`.
 */
function cellWindow({ repeat, offset }, props) {
    const H = Math.max(1, props.hframes);
    const V = Math.max(1, props.vframes);
    const { x: col, y: row } = props.frame_coords ?? frameCoords({ frame: props.frame, hframes: H });
    return {
        repeat: { x: repeat.x / H, y: repeat.y / V },
        offset: { x: offset.x + col * (repeat.x / H), y: offset.y + repeat.y - (row + 1) * (repeat.y / V) },
    };
}
//# sourceMappingURL=spriteFrame.js.map